<template>
  <LeftSide>
    <template v-slot:title>
      Edit Nomination
    </template>

    <div class="mt-2">
      <div class="bg-grey-blue p-3 rounded">
        <FormulateForm v-if="nomination"
                       name="formulateForm"
                       @created="resetFormValues"
                       @submit="handleSubmitForm"
                       v-model="formValues"
                       :errors="formErrors">
          <ifac-nomination-form :nomination="nomination"
                                :award="nomination.award"
                                :schema="formSchema"/>
        </FormulateForm>
      </div>

      <div class="row submit-buttons">
        <div class="col-md-3 p-0">
          <button
            v-if="nomination.can.cancel"
            type="submit"
            class="btn btn-light p-0"
            @click="cancelModalShow = true"
          >
            Cancel Nomination
          </button>
        </div>
        <div class="col text-right right-col p-0">
          <button
            v-if="nomination"
            type="submit"
            class="btn btn-secondary"
            :disabled="nomination.can.update === false"
            @click="saveForm"
          >
            Save
          </button>
          <button
            v-if="nomination"
            type="submit"
            class="btn btn-primary"
            :disabled="nomination.can.submit === false"
            @click="submitForm"
          >
            <span v-if="nomination.isSubmitted">Nomination submitted</span>
            <span v-else>Submit Nomination</span>
          </button>
        </div>
      </div>
    </div>

    <b-modal v-model="cancelModalShow"
             size="lg"
             title="Are you sure you want to cancel this nomination?"
             ok-title="Yes, cancel the nomination"
             cancel-title="No, I continue filling the nomination"
             @ok="cancelNomination"
             @cancel="cancelModalShow = false">
      All associated data will be permanently deleted and cannot be recovered. Please confirm to proceed.
    </b-modal>

    <b-modal v-model="refereesModalShow"
             :title="hasEnoughSelectedReferences ? 'Are you sure?' : 'Reference letters required'"
             ok-title="Yes, submit nomination"
             cancel-title="No, not ready yet"
             :ok-disabled="!hasEnoughSelectedReferences"
             @ok="submitFormForce"
             @cancel="refereesModalShow = false">
      <span v-if="selectedReferences.length === 0">
        You have not selected any reference letters to be submitted with this nomination.
        After your referees submit their letters, select them in the Reference letters panel before submitting.
      </span>
      <span v-else-if="!hasEnoughSelectedReferences">
        You have selected {{ selectedReferences.length }}/{{ nomination.referees_max }} reference letters.
        At least {{ nomination.referees_min }} must be selected before you can submit.
      </span>
      <span v-else>
        You have selected {{ selectedReferences.length }}/{{ nomination.referees_max }} reference letters to be submitted with this nomination.
      </span>
    </b-modal>
  </LeftSide>
</template>

<script>
import nominationsMixin from '@/mixins/awards/nominations.mixin';
import { ifacUiMixin, IfacNominationForm } from '@ifac/ui';
import LeftSide from '@/views/areas/awards/LeftSide.vue';
import References from '@/services/Api/Awards/References';
import { mapActions } from 'vuex';

export default {
  name: 'NominationsEdit',
  components: {
    LeftSide,
    IfacNominationForm,
  },
  mixins: [ifacUiMixin, nominationsMixin],
  props: {
    nomination: {
      type: Object,
      required: true,
    },
  },
  async created() {
    this.$store.dispatch('awards/setCurrentAward', this.nomination.award);

    this.formSchema = this.nomination.form_schema;
    this.formValues = this.nomination.form_values;
  },
  data() {
      return {
        formAction: 'save',
        formSchema: [],
        formValues: {},
        formErrors: {},
        cancelModalShow: false,
        refereesModalShow: false,
        references: [],
        forceSubmitForm: false,
      };
  },
  methods: {
    ...mapActions({
      mx_fetchReferences: 'awardsReferences/fetchReferences',
    }),
    resetFormValues() {
      // CoNominated
      if (!this.formValues.co_nominated?.length || this.formValues.co_nominated.length === 0) {
        setTimeout(() => {
          this.$set(this.formValues, 'co_nominated', []);
        }, 200);
      }

      // Referees
      if (this.nomination.hasRefereesField) {
        if (!this.formValues.referees?.length || this.formValues.referees.length === 0) {
          setTimeout(() => {
            this.$set(this.formValues, 'referees', [{ emails: [] }]);
          }, 200);
        }
      }

      // Dynamic Fields
      this.formSchema.forEach((field) => {
        if (field.type === 'group' && field.repeatable === true) {
          if (!this.formValues[field.name]?.length || this.formValues[field.name].length === 0) {
            setTimeout(() => {
              this.$set(this.formValues, field.name, []);
            }, 200);
          }
        }
      });
    },
    saveForm() {
      this.formAction = 'save';
      this.$formulate.submit('formulateForm');
    },
    submitFormForce() {
      if (!this.hasEnoughSelectedReferences) {
        return;
      }

      this.refereesModalShow = false;
      this.forceSubmitForm = true;
      this.submitForm();
    },
    submitForm() {
      this.formAction = 'submit';
      this.$formulate.submit('formulateForm');
    },
    async handleSubmitForm(data) {
      if (this.formAction === 'save') {
        try {
          await this.mx_updateNomination(data);
          this.fetchNominationReferences();
        } catch (error) {
          //
        }
      }

      if (this.formAction === 'submit') {
        if (this.forceSubmitForm === false && this.nomination.hasRefereesField) {
          const params = {
            nomination_id: this.nomination.id,
          };
          const response = await References.all(params);
          this.references = response.data.data;
          this.refereesModalShow = true;
            return;
        }

        try {
          await this.mx_submitNomination(data);
          this.fetchNominationReferences();
        } catch (error) {
          //
        } finally {
          this.forceSubmitForm = false;
        }
      }
    },
    fetchNominationReferences() {
      if (this.nomination.hasRefereesField === true) {
        this.mx_fetchReferences({
          nomination_id: this.nomination.id,
          relations: ['affiliate'],
        });
      }
    },
    cancelNomination() {
      this.mx_cancelNomination();
    },
  },
  computed: {
    selectedReferences() {
      return this.references.filter((r) => r.isSelected);
    },
    hasEnoughSelectedReferences() {
      if (!this.nomination?.hasRefereesField) {
        return true;
      }

      return this.selectedReferences.length >= this.nomination.referees_min;
    },
  },
};
</script>

<style scoped lang='scss'>
@import '@/assets/styles/base/_variables.scss';
@import '@/assets/styles/base/_mixins.scss';
</style>
